/**
 * Canonical portfolio / P&L metrics — single source for Dashboard, Master, Day Report, and drill-downs.
 * Keep formulas here so every screen shows the same figures.
 */
import { filterByRange, inRange, type DateRange } from './dateFilter';
import type { AdminEmiPayment, UnifiedCustomer } from './workspaceStore';

/** Cash applied toward EMI / loan (excludes late-fee cash and out-payments). */
export function emiCashFromPayment(
  p: Pick<
    AdminEmiPayment,
    'amount' | 'entryType' | 'lateFeeCollected' | 'lateFeeAmount' | 'flexibleEmi' | 'carriedAfter'
  >
): number {
  if (p.entryType === 'out_payment') return 0;
  const collected = Math.max(0, Number(p.lateFeeCollected) || 0);
  const charged = Math.max(0, Number(p.lateFeeAmount) || 0);
  const cash = Math.max(0, Number(p.amount) || 0);
  const lateCarve = collected > 0 ? 0 : charged;
  const emiCash = Math.max(0, cash - lateCarve);
  if (p.flexibleEmi === true || p.carriedAfter !== undefined) return emiCash;
  if (cash > 0) return emiCash;
  return 0;
}

/** Late-fee cash actually collected on this receipt (revenue ledger — no accrual double-count). */
export function lateFeeCashFromPayment(
  p: Pick<AdminEmiPayment, 'lateFeeCollected' | 'lateFeeAmount'>
): number {
  return Math.max(0, Number(p.lateFeeCollected) || 0);
}

/** Down-payment balance (out-payment) collected — real cash, but not an EMI. */
export function outPaymentCashFromPayment(p: Pick<AdminEmiPayment, 'amount' | 'entryType'>): number {
  return p.entryType === 'out_payment' ? Math.max(0, Number(p.amount) || 0) : 0;
}

/** Total cash receipt on a collection row (EMI + late fee + out-payment cash). */
export function paymentReceiptTotal(
  p: Pick<AdminEmiPayment, 'amount' | 'lateFeeCollected' | 'lateFeeAmount' | 'entryType'>
): number {
  return emiCashFromPayment(p) + lateFeeCashFromPayment(p) + outPaymentCashFromPayment(p);
}

/** Interest revenue recognized on this payment row. */
export function interestRevenueFromPayment(
  p: Pick<AdminEmiPayment, 'interestPortion' | 'amount' | 'principalPortion'>
): number {
  const interest = Math.max(0, Number(p.interestPortion) || 0);
  if (interest > 0) return interest;
  const amount = Math.max(0, Number(p.amount) || 0);
  const principal = Math.max(0, Number(p.principalPortion) || 0);
  if (amount > 0 && principal > 0 && amount >= principal) return Math.max(0, amount - principal);
  return 0;
}

export function sumPaymentsInRange(
  payments: AdminEmiPayment[],
  range: DateRange,
  pick: (p: AdminEmiPayment) => number
): number {
  return Math.round(
    payments.filter((p) => inRange(p.paidAt, range)).reduce((s, p) => s + pick(p), 0)
  );
}

export type FinancePeriodMetrics = {
  emiCollected: number;
  lateFeesCollected: number;
  totalCollections: number;
  interestCollected: number;
  principalCollected: number;
  /** Business revenue = interest + late fees (+ optional YTD seed). */
  revenue: number;
};

/** Finance ledger metrics for a date window (Master Dashboard, Day Report, exports). */
export function computeFinancePeriodMetrics(
  payments: AdminEmiPayment[],
  range: DateRange,
  ytdInterest = 0
): FinancePeriodMetrics {
  const inWin = filterByRange(payments, range, (p) => p.paidAt);
  const emiCollected = inWin.reduce((s, p) => s + emiCashFromPayment(p), 0);
  const lateFeesCollected = inWin.reduce((s, p) => s + lateFeeCashFromPayment(p), 0);
  const totalCollections = inWin.reduce((s, p) => s + paymentReceiptTotal(p), 0);
  const interestCollected = inWin.reduce((s, p) => s + interestRevenueFromPayment(p), 0);
  const principalCollected = inWin.reduce((s, p) => s + Math.max(0, Number(p.principalPortion) || 0), 0);
  const revenue = interestCollected + lateFeesCollected + Math.max(0, ytdInterest);
  return {
    emiCollected: Math.round(emiCollected),
    lateFeesCollected: Math.round(lateFeesCollected),
    totalCollections: Math.round(totalCollections),
    interestCollected: Math.round(interestCollected),
    principalCollected: Math.round(principalCollected),
    revenue: Math.round(revenue),
  };
}

/** Live portfolio totals from unified customers (same as Customers list). */
export function computePortfolioTotals(customers: UnifiedCustomer[]) {
  return {
    borrowers: customers.length,
    principal: Math.round(customers.reduce((s, c) => s + (c.totalLoan || 0), 0)),
    collected: Math.round(customers.reduce((s, c) => s + (c.paidLoan || 0), 0)),
    outstanding: Math.round(customers.reduce((s, c) => s + (c.pendingLoan || 0), 0)),
    handPrincipal: Math.round(
      customers.reduce(
        (s, c) =>
          s + c.facilities.filter((f) => f.kind === 'handloan').reduce((t, f) => t + (f.principal || 0), 0),
        0
      )
    ),
  };
}
